'use strict';
/* ==========================================================================
   levels/video.js — the explainer video, played before the lessons.
   Flow:  Title ▶ Play  →  this scene (the 60 s video)  →  the map of lessons.
   Also reachable from the map ("watch the video") for a replay.

   The video is an HTML <video> laid exactly over the canvas (the canvas can't
   decode MP4 frames cheaply), plus a paper-styled DOM "Skip" button. Both are
   removed in exit(), so Escape, Skip, the video ending, or a load error all
   leave the page clean. Game music is silenced while it plays: the video
   carries its own mixed soundtrack.
   Devices (P2): a tab switch pauses the film (core/device.js pauses every <video>; this scene opts out of the
   canvas PAUSED card, which the video would cover). A paper "▶ play" button then shows over it; if the browser
   only allowed muted playback, the same button says "tap for sound".
   ========================================================================== */
(function () {
  const SRC = 'video/newton.mp4', CAPS = 'video/newton_captions.json';   // logical, resolved through Assets.url; the music bed is Game.MUSIC (lesson.json)

  class VideoScene extends Scene {
    constructor() { super(); this.pauseCard = false; }
    enter() {
      this.done = false; this.mutedFallback = false;
      // the narration's captions (video-src/tools/film_captions.js), shown in the portrait dock
      this.caps = null;
      const cu = Assets.url(CAPS);
      (window.Loader ? Loader.json(cu) : fetch(cu).then(r => (r.ok ? r.json() : null))).then(d => { if (d && d.lines) this.caps = d; }).catch(() => {});
      Audio.music(Game.MUSIC, { gain: 0 });                 // video has its own soundtrack

      const v = this.video = document.createElement('video');
      v.src = Assets.url(SRC); v.playsInline = true; v.preload = 'auto'; v.controls = false;
      const meta = Assets.meta && Assets.meta(SRC);                  // published build: a poster frame (docs/WEBSITE.md §9)
      if (meta && meta.poster) v.poster = meta.poster;
      Object.assign(v.style, { position: 'fixed', zIndex: 10, background: '#1b1512', objectFit: 'contain' });
      v.addEventListener('ended', () => this.finish());
      v.addEventListener('error', () => this.finish());
      v.addEventListener('click', () => { v.paused ? v.play() : v.pause(); });

      const b = this.skip = document.createElement('button');
      b.textContent = 'Skip to the lessons ▶';
      Object.assign(b.style, {
        position: 'fixed', zIndex: 11, font: "30px 'Caveat Brush', cursive", color: '#fbf5e6',
        background: '#e0533d', border: '0', borderRadius: '4px', padding: '10px 26px 8px',
        boxShadow: '4px 6px 0 rgba(0,0,0,.35)', transform: 'rotate(-2deg)', cursor: 'pointer', opacity: '0.9',
      });
      b.addEventListener('click', e => { e.stopPropagation(); this.finish(); });

      // ▶ play (paused after a tab switch) / 🔈 tap for sound (muted autoplay): big enough for a thumb
      const big = this.big = document.createElement('button');
      big.type = 'button';
      Object.assign(big.style, {
        position: 'fixed', zIndex: 11, display: 'none', font: "30px 'Caveat Brush', cursive", color: '#2b2420',
        background: '#f2b33d', border: '0', borderRadius: '6px', padding: '12px 26px 10px', minHeight: '56px', minWidth: '56px',
        boxShadow: '4px 6px 0 rgba(0,0,0,.35)', transform: 'translate(-50%, -50%) rotate(-1.5deg)', cursor: 'pointer', touchAction: 'manipulation',
      });
      big.addEventListener('click', e => {
        e.stopPropagation();
        if (v.muted) { v.muted = false; this.mutedFallback = false; }
        v.play().catch(() => {});
        this.syncBig();
      });
      this.syncBig = () => {
        if (!this.video) return;
        const paused = v.paused && !v.ended && v.currentTime > 0;
        const show = (paused || this.mutedFallback) && !UI.docked();          // the portrait dock has its own ▶ and sound buttons
        big.textContent = this.mutedFallback && !paused ? '🔈 tap for sound' : '▶ play';
        big.style.display = show ? 'block' : 'none';
      };
      ['play', 'pause', 'playing', 'volumechange'].forEach(ev => v.addEventListener(ev, this.syncBig));

      document.body.append(v, b, big);
      this.place = () => {
        const r = Game.canvas.getBoundingClientRect();
        const key = [r.left, r.top, r.width, r.height, innerWidth, innerHeight].map(Math.round).join(',');
        if (key === this.placed) return;
        this.placed = key;
        Object.assign(v.style, { left: r.left + 'px', top: r.top + 'px', width: r.width + 'px', height: r.height + 'px' });
        const s = r.width / 1920;
        Object.assign(b.style, { right: (window.innerWidth - r.right + 28 * s) + 'px', bottom: (window.innerHeight - r.bottom + 28 * s) + 'px' });
        Object.assign(big.style, { left: (r.left + r.width / 2) + 'px', top: (r.top + r.height / 2) + 'px' });
      };
      this.place();
      window.addEventListener('resize', this.place);

      // The ▶ Play click is the user gesture that allows sound. If the browser still
      // refuses, fall back to muted playback rather than a frozen first frame (with a "tap for sound" button).
      v.play().catch(() => { v.muted = true; this.mutedFallback = true; this.syncBig(); v.play().catch(() => this.finish()); });
    }

    exit() {
      window.removeEventListener('resize', this.place);
      if (this.video) { this.video.pause(); this.video.removeAttribute('src'); this.video.load(); this.video.remove(); }
      if (this.skip) this.skip.remove();
      if (this.big) this.big.remove();
      this.video = this.skip = this.big = null;
      Audio.music(Game.MUSIC, { gain: 0.35 });               // bring the game bed back
    }

    finish() {
      // !this.video: the scene already left (exit() pauses and empties the element, which rejects a play() still
      // pending, e.g. its muted retry); a late finish() then must not replace whatever scene is showing now
      if (this.done || !this.video) return;
      this.done = true;
      Game.replace(new Levels.map());
    }

    keyDown(k) { if (k === 'Enter' || k === ' ' || k === 's' || k === 'S') this.finish(); }

    // The canvas sits under the video; keep it dark so the torn-paper transition
    // in and out of this scene reads as "the lights went down".
    draw() {
      g.fillStyle = '#1b1512'; g.fillRect(0, 0, Game.W, Game.H);
      if (this.video && this.video.readyState < 2) hand('loading the film…', 960, 540, 60, this.enteredAt, { color: PAL.cream, dur: 0.6 });
      const docked = UI.docked();
      if (this.skip) {
        // the layout moves when the portrait dock comes or goes: keep the film on the stage, the overlay buttons off the dock
        if (this.place) this.place();
        if (docked !== this.wasDocked) { this.wasDocked = docked; this.skip.style.display = docked ? 'none' : ''; this.syncBig(); }
      }
      if (docked && this.video) this.drawDock(this.video);
    }

    /* the portrait dock (core/dock.js): film controls below the stage */
    drawDock(v) {
      const step = (Game.lesson.steps || []).find(s => s.id === 'video') || {};
      const dur = isFinite(v.duration) && v.duration > 0 ? v.duration : 60, now = Math.min(dur, v.currentTime || 0);
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(step.title || 'The film', `${mmss(now)} / ${mmss(dur)}`);
      // the narration, word by word (the line being spoken, or the last one, dimmed, between lines)
      const L = this.caps ? this.caps.lines : [];
      let li = -1;
      for (let i = 0; i < L.length; i++) if (now >= L[i].start - 0.05) li = i;
      if (li >= 0) {
        const l = L[li];
        Dock.caption({ key: 'film:' + l.id, who: 'narrator', name: 'Narrator', color: PAL.navy, words: l.words, t: now, done: now > l.end + 0.4,
          prev: L.slice(Math.max(0, li - 1), li).map(x => ({ name: 'Narrator', color: PAL.navy, text: x.text })) });
      }
      const lines = [];
      if (li < 0) lines.push(step.tag ? step.tag.charAt(0).toUpperCase() + step.tag.slice(1) + '.' : '');
      if (v.readyState < 2) lines.push('Loading the film…');
      else if (v.muted) lines.push('Playing without sound: tap 🔈 for sound.');
      else if (v.paused && !v.ended) lines.push('Paused: tap ▶ to carry on.');
      Dock.text('film', lines, { cls: 'note center' });
      Dock.buttons('film', [{ label: 'Skip to the lessons ▶', color: PAL.red, wide: true, onClick: () => this.finish() }], { bottom: true });
      Dock.strip({ value: now, total: dur, label: 'film position', onSeek: (x, phase) => {
        if (phase === 'start') this.scrubWas = !v.paused;
        try { v.currentTime = Math.max(0, Math.min(dur - 0.1, x)); } catch (e) { /* not seekable yet */ }
        if (phase === 'end' && this.scrubWas) v.play().catch(() => {});
      } });
      Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the film from the start', onClick: () => { v.currentTime = 0; v.play().catch(() => {}); } });
      Dock.button({ id: 'play', glyph: v.paused ? '▶' : '❚❚', label: v.paused ? 'play' : 'pause', aria: v.paused ? 'play' : 'pause', primary: true, onClick: () => {
        if (v.paused) { if (this.mutedFallback) { v.muted = false; this.mutedFallback = false; } v.play().catch(() => {}); } else v.pause();
      } });
      Dock.button({ id: 'sound', glyph: v.muted ? '🔇' : '🔈', label: v.muted ? 'sound off' : 'sound on', aria: 'sound', on: !v.muted, onClick: () => {
        v.muted = !v.muted; this.mutedFallback = false; if (!v.muted && v.paused) v.play().catch(() => {});
      } });
    }
  }

  Levels.video = VideoScene;
})();
